import { useCallback, useMemo } from "react";

import { useUserSetting } from "#app/lib/hooks/use-user-setting";

/**
 * Per-user display order for tab rows (the workflow layout's view tabs and
 * the detail dialogs' tab strips).
 * Persisted in the generic user settings bag (`view-tab-order:<scope>`), so
 * the order follows the user across workspaces. The saved value is normalized
 * against the scope's canonical tab set: unknown and duplicate entries drop,
 * missing tabs append in default order — so a tab added later never strands,
 * and a stale saved array can never hide a tab.
 */

/** Every team tab that has a route of its own. */
export type TeamViewTab = "overview" | "members" | "chart" | "board" | "list";

/**
 * The tabs the team strip shows: the anchors — Overview owns the canvas, the
 * roster tabs (Members, Team Chart) sit beside it, then one tab per record
 * kind the team is connected to: Projects (the `list` route) today, Accounts
 * and Contacts after it once they carry Teams. Each kind tab lists what is
 * assigned to or shared with the team. Board keeps its route without a seat:
 * a team spans workflows, so its projects read as one table. The strip and
 * the index redirect both read this, so a remembered tab is judged against
 * the same set it renders.
 */
export const TEAM_STRIP_TABS = [
  "overview",
  "members",
  "chart",
  "list",
] as const satisfies readonly TeamViewTab[];

function normalizeOrder<T extends string>(
  value: unknown,
  all: readonly T[],
): T[] {
  const canonical = new Set<string>(all);
  const next: T[] = [];
  if (Array.isArray(value)) {
    for (const entry of value) {
      if (
        typeof entry === "string" &&
        canonical.has(entry) &&
        !next.includes(entry as T)
      ) {
        next.push(entry as T);
      }
    }
  }
  for (const tab of all) {
    if (!next.includes(tab)) next.push(tab);
  }
  return next;
}

// Module-level parser — useUserSetting derives its value off parse identity.
const parseKeys = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === "string")
    : [];

/**
 * How the pinned run is sequenced. `anchored`: the `pinned` array's own
 * sequence — a fixed set of lead tabs (Summary, a team's Overview) that never
 * drag. `sorted`: the saved order's sequence, so a run whose members are data
 * (a workflow's pinned views) drags among itself like the rest of the strip,
 * while pinning still decides membership of the run.
 */
type PinnedSequence = "anchored" | "sorted";

function useViewTabOrderForKey<T extends string>(
  key: string,
  all: readonly T[],
  pinned: readonly T[] | undefined,
  pinnedSequence: PinnedSequence,
) {
  const { value: saved, setValue } = useUserSetting(key, parseKeys);

  // Pinned tabs hoist to the front no matter where a previously saved order
  // put them — pinning applied after the fact must win over a stale saved
  // array. Inside the run, `pinnedSequence` picks whose sequence holds; the
  // rest keep their saved sequence. Pass `all` and `pinned` as module-level
  // (or memoized) constants so this memo holds.
  const order = useMemo(() => {
    const normalized = normalizeOrder(saved, all);
    if (!pinned?.length) return normalized;
    const pinnedSet = new Set<T>(pinned);
    const run =
      pinnedSequence === "sorted"
        ? normalized.filter((tab) => pinnedSet.has(tab))
        : pinned.filter((tab) => normalized.includes(tab));
    return [...run, ...normalized.filter((tab) => !pinnedSet.has(tab))];
  }, [saved, all, pinned, pinnedSequence]);

  // Reorders arrive from the tab row as the visible SORTABLE sequence — every
  // run the row drags, in row order; anchored tabs are never in the row's
  // `order`, and a tab the row hides is not in it either. Splice the new
  // sequence back into the full order so anchored and hidden tabs keep their
  // slots.
  const reorderVisible = useCallback(
    (nextVisible: string[]) => {
      const visibleSet = new Set(nextVisible);
      let cursor = 0;
      setValue(
        order.map((tab) =>
          visibleSet.has(tab) ? (nextVisible[cursor++] as T) : tab,
        ),
      );
    },
    [order, setValue],
  );

  return { order, reorderVisible };
}

/**
 * Per-user tab order for a scope whose tab set is DATA rather than a constant
 * — a workflow's saved views, which anyone with `create.views` can add to.
 *
 * The canonical sequence is the server's `position` order, passed in as
 * `all`; this layers one person's drag order over it. That split is what
 * keeps reordering a per-user preference: a member with no `create.views`
 * still arranges their own tabs, and a view someone else adds appends in
 * server order instead of stranding. Pass a MEMOIZED `all`.
 *
 * `pinned` is the scope's pinned run — the rows' `isPinned`, the same set
 * for every member: those ids hoist to the front ahead of the rest.
 * Membership is all it decides — inside the run the saved sequence holds, so
 * the strip hands the run to the row's `order` as a run of its own and the
 * viewer drags pinned views among themselves.
 *
 * The key carries the scope's id — view ids are per-scope, so one shared key
 * would normalize every workflow's order down to nothing.
 */
export function useEntityViewTabOrder(
  scopeKey: string,
  all: readonly string[],
  pinned?: readonly string[],
) {
  return useViewTabOrderForKey(
    `view-tab-order:${scopeKey}`,
    all,
    pinned,
    "sorted",
  );
}

/** The detail surfaces' shared anchor: Summary always leads and never drags. */
export const DETAIL_SUMMARY_PINNED = ["summary"] as const;

/**
 * Per-user tab order for an entity detail surface (the project view). One key
 * per surface — their tab vocabularies differ, so the orders don't share.
 * Pass the surface's canonical tab array and its pinned set
 * (`DETAIL_SUMMARY_PINNED`) as module-level constants. Pinned tabs always
 * render first (left) and can't be dragged or displaced, so they are excluded
 * from the `order` prop handed to `TabsList` — membership in that prop is what
 * makes a tab draggable.
 */
export function useDetailViewTabOrder<T extends string>(
  view: "project",
  all: readonly T[],
  pinned: readonly T[],
) {
  return useViewTabOrderForKey<T>(
    `view-tab-order:${view}-detail`,
    all,
    pinned,
    "anchored",
  );
}
